import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  BarChart3,
  Loader2,
  CalendarCheck,
  PhoneCall,
  UserX,
  RefreshCcw,
  FileText,
  UserMinus,
  Repeat,
  Send,
  Clock,
  TrendingUp,
  CheckCircle2,
  AlertCircle,
  Calendar as CalendarIcon,
  Trash2,
  Plus,
  ExternalLink,
  Users,
  GitMerge,
} from "lucide-react";
import { api } from "@/lib/api";
import { useNavigate } from "react-router-dom";

const ICONS = {
  call_realmente_fatte: PhoneCall,
  no_show: UserX,
  no_show_recuperati: RefreshCcw,
  contratti_totali: FileText,
  rinunce: UserMinus,
  second_call: Repeat,
  contratti_inviati: Send,
  chiedono_tempo: Clock,
  rimanda: Clock,
  scomparso_1: UserX,
  scomparso_2: UserX,
  non_fissata: CalendarCheck,
  attesa_firma: FileText,
  scomparso_recuperati: RefreshCcw,
};

const ACCENTS = {
  call_realmente_fatte: "border-emerald-600 bg-emerald-50 text-emerald-700",
  no_show: "border-red-600 bg-red-50 text-red-700",
  no_show_recuperati: "border-amber-600 bg-amber-50 text-amber-700",
  contratti_totali: "border-[#0A0A0A] bg-[#F2F2EC] text-[#0A0A0A]",
  rinunce: "border-amber-800 bg-amber-50 text-amber-800",
  second_call: "border-[#FF5A1F] bg-orange-50 text-[#FF5A1F]",
  contratti_inviati: "border-indigo-600 bg-indigo-50 text-indigo-700",
  chiedono_tempo: "border-sky-600 bg-sky-50 text-sky-700",
  rimanda: "border-indigo-700 bg-indigo-50 text-indigo-800",
  scomparso_1: "border-rose-600 bg-rose-50 text-rose-700",
  scomparso_2: "border-rose-800 bg-rose-50 text-rose-900",
  non_fissata: "border-slate-500 bg-slate-50 text-slate-700",
  attesa_firma: "border-emerald-700 bg-emerald-50 text-emerald-800",
  scomparso_recuperati: "border-rose-500 bg-rose-50 text-rose-700",
};

export default function AdminReportKPI() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Analisi eventi calendar — stato PERSISTITO in localStorage
  const STORAGE_KEY = "perla.reportKpi.calendar";
  const loadStoredCal = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };
  const stored = loadStoredCal() || {};
  const [calStart, setCalStart] = useState(stored.calStart || "2026-08-01");
  const [calEnd, setCalEnd] = useState(stored.calEnd || new Date().toISOString().slice(0, 10));
  const [calIncludeHidden, setCalIncludeHidden] = useState(!!stored.calIncludeHidden);
  const [calData, setCalData] = useState(stored.calData || null);
  const [calLoadedAt, setCalLoadedAt] = useState(stored.calLoadedAt || null);
  const [calLoading, setCalLoading] = useState(false);
  const [calError, setCalError] = useState("");
  const [rowBusy, setRowBusy] = useState({}); // {eventId: true}

  // Drilldown KPI: modal con elenco schede (ora pesca da data.leads_by_bucket)
  const [drilldown, setDrilldown] = useState(null); // { kpi, items, loading, isContracts }

  // Mappa KPI → lista di bucket keys usati dal backend. "call_realmente_fatte"
  // aggrega 12 bucket (tutti i lead che hanno avuto una call con un esito).
  const kpiToBuckets = (k) => {
    switch (k.key) {
      case "call_realmente_fatte":
        return [
          "signed", "call_back", "no_call", "next_call", "rinuncia", "rimanda",
          "attesa_firma", "da_contattare", "scomparso_1", "scomparso_2",
          "scomparso_recovered", "no_show_recovered",
        ];
      case "no_show":                 return ["no_show"];
      case "no_show_recuperati":      return ["no_show_recovered"];
      case "contratti_totali":        return "__contracts__";
      case "rinunce":                 return ["rinuncia"];
      case "second_call":             return ["call_back"];
      case "chiedono_tempo":          return ["next_call"];
      case "rimanda":                 return ["rimanda"];
      case "scomparso_1":             return ["scomparso_1"];
      case "scomparso_2":             return ["scomparso_2"];
      case "scomparso_recuperati":    return ["scomparso_recovered"];
      case "non_fissata":             return ["non_fissata"];
      case "attesa_firma":            return ["attesa_firma"];
      case "da_contattare":           return ["da_contattare"];
      default:                        return null;
    }
  };

  const openDrilldown = (kpi) => {
    const key = kpiToBuckets(kpi);
    if (!key) return;
    if (key === "__contracts__") {
      // Clienti con contratto: lista contratti con flag has_matching_card.
      // Ordinamento: orfani prima (rossi), poi gli altri.
      const detail = (data?.contracts_detail || []).slice().sort((a, b) => {
        if (a.has_matching_card !== b.has_matching_card) return a.has_matching_card ? 1 : -1;
        return (a.artist_name || "").localeCompare(b.artist_name || "");
      });
      // Raggruppa per artist_name (un cliente può avere più contratti)
      const byName = {};
      detail.forEach((c) => {
        const n = c.artist_name || "—";
        if (!byName[n]) byName[n] = { artist_name: n, has_matching_card: c.has_matching_card, contracts: [] };
        byName[n].contracts.push(c);
      });
      const items = Object.values(byName).sort((a, b) => {
        if (a.has_matching_card !== b.has_matching_card) return a.has_matching_card ? 1 : -1;
        return a.artist_name.localeCompare(b.artist_name);
      });
      setDrilldown({ kpi, items, loading: false, isContracts: true });
      return;
    }
    // Bucket "normali": leggi i lead dal payload KPI
    const buckets = data?.leads_by_bucket || {};
    const items = key.flatMap((b) => buckets[b] || []);
    setDrilldown({ kpi, items, loading: false, isContracts: false });
  };

  // Deduplica schede
  const [dupData, setDupData] = useState(null);
  const [dupLoading, setDupLoading] = useState(false);
  const [dupMerging, setDupMerging] = useState({}); // {groupKey: true}

  // Scrive in localStorage ogni volta che la lista o i parametri cambiano
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ calStart, calEnd, calIncludeHidden, calData, calLoadedAt })
      );
    } catch {}
  }, [calStart, calEnd, calIncludeHidden, calData, calLoadedAt]);

  const load = async () => {
    setLoading(true);
    try {
      // Passo start/end così il backend applica la REGOLA FERREA:
      // ogni evento calendar visibile nel range = 1 Lead Unico.
      const params = {};
      if (calStart) params.start = calStart;
      if (calEnd) params.end = calEnd;
      const r = await api.get("/mio-giorno/report-kpi", { params });
      setData(r.data);
    } catch (e) {
      toast.error("Errore caricamento report KPI");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // Se c'è una cache dell'audit calendar, aggiorno solo gli status senza ricaricare Google
    if (stored.calData && stored.calData.events && stored.calData.events.length > 0) {
      refreshCardStatuses(stored.calData.events);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshCardStatuses = async (events) => {
    const ids = events.map((e) => e.id).filter(Boolean);
    if (ids.length === 0) return;
    try {
      const r = await api.post("/mio-giorno/cards/by-event-ids", { event_ids: ids });
      const cards = r.data?.cards || [];
      const byEvId = Object.fromEntries(cards.map((c) => [c.calendar_event_id, c]));
      setCalData((d) => {
        if (!d) return d;
        let withCard = 0;
        const newEvents = d.events.map((ev) => {
          const card = byEvId[ev.id];
          if (card) {
            withCard++;
            return {
              ...ev,
              has_card: true,
              card_id: card.id,
              card_artist: card.artist_name,
              card_status: card.status,
              card_status_reason: card.status_reason,
              card_date: card.date,
            };
          }
          return { ...ev, has_card: false, card_id: null, card_artist: null, card_status: null };
        });
        return { ...d, events: newEvents, with_card: withCard, without_card: newEvents.length - withCard };
      });
    } catch (e) {
      /* silent */
    }
  };

  const loadDuplicates = async () => {
    setDupLoading(true);
    try {
      const r = await api.get("/mio-giorno/duplicates");
      setDupData(r.data);
    } catch (e) {
      toast.error("Errore scansione duplicati");
    } finally {
      setDupLoading(false);
    }
  };

  const mergeGroup = async (group) => {
    if (!window.confirm(
      `Unire ${group.to_merge.length} scheda/e nella principale "${group.primary.artist_name}"?\n\n` +
      `La scheda principale è quella con più informazioni (score ${group.primary.score}).\n` +
      `Le altre verranno fuse (note, voice note, timeline, contratti) e poi eliminate.\n\n` +
      `Operazione IRREVERSIBILE. Confermi?`
    )) return;
    setDupMerging((prev) => ({ ...prev, [group.normalized_name]: true }));
    try {
      const r = await api.post("/mio-giorno/duplicates/merge", {
        primary_id: group.primary.id,
        duplicate_ids: group.to_merge.map((d) => d.id),
      });
      toast.success(`${r.data?.merged || 0} schede fuse in "${group.primary.artist_name}"`);
      await loadDuplicates();
      load(); // ricarica KPI totale
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante la fusione");
    } finally {
      setDupMerging((prev) => ({ ...prev, [group.normalized_name]: false }));
    }
  };

  const loadCalendar = async () => {
    setCalLoading(true);
    setCalError("");
    try {
      const r = await api.get("/mio-giorno/calendar/events-list", {
        params: { start: calStart, end: calEnd, include_hidden: calIncludeHidden },
      });
      setCalData(r.data);
      setCalLoadedAt(new Date().toISOString());
    } catch (e) {
      const msg = e?.response?.data?.detail || "Errore caricamento eventi";
      setCalError(msg);
      toast.error(msg);
    } finally {
      setCalLoading(false);
    }
  };

  const clearCalendar = () => {
    if (!window.confirm("Svuotare la lista cache? Dovrai cliccare 'Carica elenco eventi' per ricaricarla.")) return;
    setCalData(null);
    setCalLoadedAt(null);
    setCalError("");
  };

  // Estrae un nome artista "pulito" dal titolo evento (prova a prendere il contenuto
  // tra parentesi se presente, es: "La Perla Music (Mario Rossi)" -> "Mario Rossi")
  const extractArtistName = (summary) => {
    const s = (summary || "").trim();
    const m = s.match(/\(([^)]+)\)/);
    if (m) return m[1].trim();
    return s || "Senza titolo";
  };

  const setBusy = (id, v) =>
    setRowBusy((prev) => ({ ...prev, [id]: v }));

  const hideEvent = async (ev) => {
    if (!window.confirm(`Nascondi "${ev.summary || "evento"}" da questo elenco?\n\nL'evento resta su Google Calendar ma non comparirà più nel CRM.`)) return;
    setBusy(ev.id, true);
    try {
      await api.post("/mio-giorno/calendar/hide-event", { event_id: ev.id });
      toast.success("Evento nascosto dal CRM");
      // Rimuovi la riga localmente senza ricaricare tutto
      setCalData((d) => ({
        ...d,
        events: d.events.filter((e) => e.id !== ev.id),
        total: d.total - 1,
        with_card: d.with_card - (ev.has_card ? 1 : 0),
        without_card: d.without_card - (ev.has_card ? 0 : 1),
        hidden_excluded: d.hidden_excluded + 1,
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setBusy(ev.id, false);
    }
  };

  const createCardFromEvent = async (ev) => {
    const name = window.prompt(
      "Nome artista per la scheda CRM:",
      extractArtistName(ev.summary)
    );
    if (!name || !name.trim()) return;
    setBusy(ev.id, true);
    try {
      const r = await api.post("/mio-giorno/card", {
        date: ev.date,
        artist_name: name.trim(),
        calendar_event_id: ev.id,
        scheduled_time: ev.time || null,
      });
      toast.success(`Scheda creata per ${name.trim()}`, {
        action: {
          label: "Apri",
          onClick: () => openCard(ev.date, r.data?.id),
        },
      });
      // Aggiorna la riga: ora ha card
      setCalData((d) => ({
        ...d,
        events: d.events.map((e) =>
          e.id === ev.id
            ? {
                ...e,
                has_card: true,
                card_id: r.data?.id,
                card_artist: name.trim(),
                card_status: null,
                card_date: ev.date,
              }
            : e
        ),
        with_card: d.with_card + 1,
        without_card: Math.max(0, d.without_card - 1),
      }));
      // Ricarica i KPI in alto per riflettere il nuovo totale
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione scheda");
    } finally {
      setBusy(ev.id, false);
    }
  };

  const openCard = (date, cardId) => {
    navigate("/admin/mio-giorno", { state: { scroll_to_card: { date, cardId } } });
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("mg-jump-to-card", { detail: { date, cardId } }));
    }, 150);
  };

  if (loading && !data)
    return (
      <div className="p-10 text-sm text-[#0A0A0A]/50 flex items-center gap-2">
        <Loader2 size={16} className="animate-spin" /> Carico i tuoi KPI…
      </div>
    );
  if (!data) return null;

  const { total_leads, kpis, extra } = data;

  return (
    <div className="max-w-6xl mx-auto p-6 md:p-10" data-testid="admin-report-kpi">
      <header className="mb-8">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#FF5A1F]">
          <BarChart3 size={14} /> Report attività
        </div>
        <h1 className="mt-2 font-display text-4xl md:text-5xl uppercase text-[#0A0A0A]">
          I tuoi numeri
        </h1>
        <p className="mt-2 text-sm text-[#0A0A0A]/60 max-w-2xl">
          Statistiche in tempo reale sulle tue schede CRM. Le percentuali sono calcolate
          sul <b>totale lead unici</b> (schede principali, escluse le riprogrammazioni).
        </p>
      </header>

      {/* Totale lead */}
      <div
        className="mb-6 border-2 border-[#0A0A0A] bg-[#0A0A0A] text-white p-5"
        data-testid="kpi-total-leads"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/60 mb-1">
              Totale lead unici
            </div>
            <div className="font-display text-4xl md:text-5xl">{total_leads}</div>
            <div className="text-[9px] uppercase tracking-widest text-white/40 mt-1">
              call uniche ricevute dal calendar (senza doppioni/riprogrammazioni)
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2 text-sm text-white/70">
            <TrendingUp size={16} /> base % di tutti i KPI
          </div>
        </div>
      </div>

      {/* Grid 9 KPI */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {kpis.map((k) => {
          const Icon = ICONS[k.key] || BarChart3;
          const cls = ACCENTS[k.key] || "border-[#0A0A0A]/40 bg-white text-[#0A0A0A]";
          return (
            <button
              type="button"
              key={k.key}
              onClick={() => openDrilldown(k)}
              className={`text-left border-2 p-4 hover:shadow-lg transition cursor-pointer ${cls}`}
              data-testid={`kpi-${k.key}`}
              title={`${k.desc} — Clicca per vedere l'elenco`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="text-[10px] uppercase tracking-widest font-bold leading-tight">
                  {k.label}
                </div>
                <Icon size={18} className="shrink-0 opacity-70" />
              </div>
              <div className="flex items-end justify-between gap-3">
                <div className="font-display text-4xl leading-none">{k.count}</div>
                <div className="text-right">
                  <div className="text-xs font-bold leading-none">{k.pct}%</div>
                  <div className="text-[9px] uppercase tracking-widest opacity-60 mt-1">
                    sul totale
                  </div>
                </div>
              </div>
              <div className="mt-3 text-[11px] opacity-70 leading-snug">{k.desc}</div>
              {k.breakdown && Object.values(k.breakdown).some((v) => v > 0) && (
                <div className="mt-2 flex flex-wrap gap-1" data-testid={`breakdown-${k.key}`}>
                  {Object.entries(k.breakdown).map(([reason, count]) =>
                    count > 0 ? (
                      <span
                        key={reason}
                        className="inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-wider bg-white/70 border border-current rounded-sm"
                      >
                        {reason}: {count}
                      </span>
                    ) : null
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Extra stats rimosse: non servivano all'utente */}

      <div className="mt-10 flex items-center gap-3">
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 border-2 border-[#0A0A0A] px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[#0A0A0A] hover:text-white transition disabled:opacity-50"
          data-testid="btn-reload-kpi"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCcw size={12} />}
          Aggiorna dati
        </button>
      </div>

      {/* ------------------------------------------------------------------
          UNIFICA DUPLICATI
          ----------------------------------------------------------------*/}
      <section className="mt-14 border-t-2 border-[#0A0A0A]/15 pt-10" data-testid="duplicates-section">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#FF5A1F] mb-2">
          <Users size={14} /> Pulizia schede
        </div>
        <h2 className="font-display text-3xl md:text-4xl uppercase text-[#0A0A0A]">
          Unifica schede duplicate
        </h2>
        <p className="mt-2 text-sm text-[#0A0A0A]/60 max-w-2xl">
          Scansiona le schede con lo <b>stesso nome</b> e le unisce in una sola, preservando
          note, voice note, timeline e contratti collegati. La "principale" è scelta in base
          alla ricchezza di informazioni (voice note, status, campi compilati…).
        </p>
        <div className="mt-4">
          <button
            onClick={loadDuplicates}
            disabled={dupLoading}
            className="inline-flex items-center gap-2 border-2 border-[#0A0A0A] px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-[#0A0A0A] hover:text-white transition disabled:opacity-50"
            data-testid="btn-scan-duplicates"
          >
            {dupLoading ? <Loader2 size={12} className="animate-spin" /> : <GitMerge size={12} />}
            Scansiona duplicati
          </button>
        </div>

        {dupData && (
          <div className="mt-6" data-testid="duplicates-results">
            {dupData.groups.length === 0 ? (
              <div className="border-2 border-emerald-600 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-center gap-2">
                <CheckCircle2 size={14} /> Nessun duplicato trovato. Le tue schede sono pulite.
              </div>
            ) : (
              <>
                <div className="text-sm mb-4">
                  Trovati <b>{dupData.groups.length}</b> gruppi con <b>{dupData.total_duplicates}</b> schede duplicate.
                </div>
                <div className="space-y-4">
                  {dupData.groups.map((g) => {
                    const busy = !!dupMerging[g.normalized_name];
                    return (
                      <div
                        key={g.normalized_name}
                        className="border-2 border-[#0A0A0A]/30 bg-white p-4"
                        data-testid={`dup-group-${g.normalized_name}`}
                      >
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div>
                            <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold">
                              {g.primary.artist_name}
                            </div>
                            <div className="text-xs text-[#0A0A0A]/60 mt-1">
                              {g.to_merge.length + 1} schede con nome identico
                            </div>
                          </div>
                          <button
                            onClick={() => mergeGroup(g)}
                            disabled={busy}
                            className="btn-cta text-xs disabled:opacity-50"
                            data-testid={`merge-group-${g.normalized_name}`}
                          >
                            {busy ? <Loader2 size={10} className="animate-spin" /> : <GitMerge size={10} />}
                            Unifica
                          </button>
                        </div>
                        <div className="mt-3 grid md:grid-cols-2 gap-2 text-xs">
                          <div className="border-2 border-emerald-600 bg-emerald-50 p-2">
                            <div className="text-[9px] uppercase tracking-widest text-emerald-700 font-bold mb-1">
                              Principale (score {g.primary.score})
                            </div>
                            <div className="text-emerald-900">
                              {g.primary.date} · {g.primary.status || "no status"} · {g.primary.voice_notes} voice · {g.primary.text_notes} note
                            </div>
                          </div>
                          <div className="border border-[#0A0A0A]/30 p-2">
                            <div className="text-[9px] uppercase tracking-widest text-[#0A0A0A]/60 font-bold mb-1">
                              Da fondere ({g.to_merge.length})
                            </div>
                            {g.to_merge.map((d) => (
                              <div key={d.id} className="text-[#0A0A0A]/70">
                                {d.date} · {d.status || "no status"} · {d.voice_notes}v {d.text_notes}n (score {d.score})
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </section>

      {/* ------------------------------------------------------------------
          DRILLDOWN KPI (modal con elenco schede)
          ----------------------------------------------------------------*/}
      {drilldown && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto"
          onClick={() => setDrilldown(null)}
          data-testid="drilldown-overlay"
        >
          <div
            className="bg-white border-2 border-[#0A0A0A] w-full max-w-3xl mt-10 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b-2 border-[#0A0A0A] flex items-center justify-between bg-[#F2F2EC]">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-[#FF5A1F]">Elenco</div>
                <h3 className="font-display text-2xl text-[#0A0A0A]">{drilldown.kpi.label}</h3>
                <div className="text-xs text-[#0A0A0A]/60 mt-1">{drilldown.kpi.desc}</div>
              </div>
              <button
                onClick={() => setDrilldown(null)}
                className="text-2xl text-[#0A0A0A]/50 hover:text-[#0A0A0A]"
                data-testid="drilldown-close"
              >
                ×
              </button>
            </div>
            <div className="p-4 max-h-[70vh] overflow-y-auto">
              {drilldown.loading ? (
                <div className="flex items-center gap-2 text-sm text-[#0A0A0A]/60">
                  <Loader2 size={14} className="animate-spin" /> Carico elenco…
                </div>
              ) : drilldown.cards.length === 0 ? (
                <div className="text-sm text-[#0A0A0A]/50">Nessun elemento in questa categoria.</div>
              ) : (
                <div className="divide-y divide-[#0A0A0A]/10">
                  {drilldown.cards.map((c, i) => (
                    <div key={c.id || i} className="py-2 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-medium text-sm text-[#0A0A0A]">
                          {c.artist_name}
                          {c.parent_card_id && (
                            <span className="ml-2 text-[9px] uppercase tracking-widest bg-blue-100 text-blue-800 px-1 rounded">
                              rischedulata
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-[#0A0A0A]/60 font-mono">
                          {c.date || "—"}
                          {c.status_reason && <span className="ml-2">· motivo: {c.status_reason}</span>}
                          {c.no_show_recovered_to && <span className="ml-2 text-emerald-700">· recuperato: {c.no_show_recovered_to}</span>}
                          {c.scomparso_recovered_to && <span className="ml-2 text-emerald-700">· recuperato: {c.scomparso_recovered_to}</span>}
                          {c.contracts && <span className="ml-2">· {c.contracts.length} contratti</span>}
                        </div>
                      </div>
                      {c.id && c.date && (
                        <button
                          onClick={() => {
                            setDrilldown(null);
                            openCard(c.date, c.id);
                          }}
                          className="px-2 py-1 text-[10px] uppercase tracking-widest font-bold border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition"
                          data-testid={`open-drilldown-card-${c.id}`}
                        >
                          Apri
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------
          ANALISI EVENTI CALENDAR
          ----------------------------------------------------------------*/}
      <section className="mt-14 border-t-2 border-[#0A0A0A]/15 pt-10" data-testid="calendar-audit">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-[#FF5A1F] mb-2">
          <CalendarIcon size={14} /> Audit calendario
        </div>
        <h2 className="font-display text-3xl md:text-4xl uppercase text-[#0A0A0A]">
          Call programmate dal calendar
        </h2>
        <p className="mt-2 text-sm text-[#0A0A0A]/60 max-w-2xl">
          Elenco grezzo degli eventi del tuo Google Calendar in un range di date, con
          indicazione se ciascun evento è stato convertito in <b>scheda CRM</b> oppure no.
          Serve a scoprire gli eventi "dimenticati" (es. 50 call sul calendar ma solo
          16 schede CRM).
        </p>

        <div className="mt-5 flex flex-wrap items-end gap-3 bg-[#F2F2EC] border-2 border-[#0A0A0A] p-4">
          <div>
            <label className="text-[9px] uppercase tracking-widest text-[#0A0A0A]/60 block">
              Dal
            </label>
            <input
              type="date"
              value={calStart}
              onChange={(e) => setCalStart(e.target.value)}
              className="mt-1 border-2 border-[#0A0A0A] bg-white px-2 py-1 text-sm font-mono"
              data-testid="cal-start"
            />
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-widest text-[#0A0A0A]/60 block">
              Al
            </label>
            <input
              type="date"
              value={calEnd}
              onChange={(e) => setCalEnd(e.target.value)}
              className="mt-1 border-2 border-[#0A0A0A] bg-white px-2 py-1 text-sm font-mono"
              data-testid="cal-end"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-[#0A0A0A]/70 cursor-pointer">
            <input
              type="checkbox"
              checked={calIncludeHidden}
              onChange={(e) => setCalIncludeHidden(e.target.checked)}
              data-testid="cal-include-hidden"
            />
            Includi eventi auto-nascosti (hotel/voli/ecc.)
          </label>
          <button
            onClick={loadCalendar}
            disabled={calLoading}
            className="btn-cta text-xs disabled:opacity-60"
            data-testid="btn-load-calendar"
          >
            {calLoading ? <Loader2 size={12} className="animate-spin" /> : <CalendarIcon size={12} />}
            {calData ? "Ricarica elenco" : "Carica elenco eventi"}
          </button>
          {calData && (
            <button
              onClick={clearCalendar}
              className="text-xs text-[#0A0A0A]/50 hover:text-red-600 underline"
              data-testid="btn-clear-calendar-cache"
              title="Svuota la cache locale della lista"
            >
              Svuota
            </button>
          )}
          {calLoadedAt && (
            <div className="text-[10px] text-[#0A0A0A]/50 ml-auto">
              Ultimo caricamento:{" "}
              <span className="font-mono text-[#0A0A0A]/70">
                {new Date(calLoadedAt).toLocaleString("it-IT")}
              </span>
            </div>
          )}
        </div>

        {calError && (
          <div className="mt-4 border-2 border-red-600 bg-red-50 p-3 text-sm text-red-700 flex items-center gap-2">
            <AlertCircle size={14} /> {calError}
          </div>
        )}

        {calData && (
          <div className="mt-6" data-testid="cal-results">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <ExtraStat label="Eventi totali" value={calData.total} />
              <ExtraStat label="Con scheda CRM" value={calData.with_card} />
              <ExtraStat label="Senza scheda CRM" value={calData.without_card} />
              <ExtraStat label="Nascosti esclusi" value={calData.hidden_excluded} />
            </div>

            <div className="overflow-x-auto border-2 border-[#0A0A0A]">
              <table className="min-w-full text-sm" data-testid="cal-events-table">
                <thead className="bg-[#0A0A0A] text-white text-[10px] uppercase tracking-widest">
                  <tr>
                    <th className="p-2 text-left">#</th>
                    <th className="p-2 text-left">Data</th>
                    <th className="p-2 text-left">Ora</th>
                    <th className="p-2 text-left">Titolo evento</th>
                    <th className="p-2 text-left">Scheda CRM</th>
                    <th className="p-2 text-left">Status</th>
                    <th className="p-2 text-right">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {calData.events.length === 0 && (
                    <tr>
                      <td colSpan="7" className="p-6 text-center text-[#0A0A0A]/50">
                        Nessun evento nel range selezionato
                      </td>
                    </tr>
                  )}
                  {calData.events.map((ev, i) => {
                    const busy = !!rowBusy[ev.id];
                    return (
                      <tr key={ev.id || i} className="border-t border-[#0A0A0A]/10 hover:bg-[#F2F2EC]">
                        <td className="p-2 font-mono text-xs text-[#0A0A0A]/50">{i + 1}</td>
                        <td className="p-2 font-mono text-xs whitespace-nowrap">{ev.date}</td>
                        <td className="p-2 font-mono text-xs whitespace-nowrap">{ev.time || "—"}</td>
                        <td className="p-2 text-sm font-medium">
                          {ev.summary || <span className="text-[#0A0A0A]/40">(senza titolo)</span>}
                        </td>
                        <td className="p-2">
                          {ev.has_card ? (
                            <span className="inline-flex items-center gap-1 text-xs text-green-700 font-bold">
                              <CheckCircle2 size={12} /> {ev.card_artist || "sì"}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-red-600 font-bold">
                              <AlertCircle size={12} /> MANCANTE
                            </span>
                          )}
                        </td>
                        <td className="p-2 text-xs">
                          {ev.card_status ? (
                            <span>
                              {ev.card_status}
                              {ev.card_status_reason && (
                                <span className="opacity-60"> · {ev.card_status_reason}</span>
                              )}
                            </span>
                          ) : (
                            <span className="text-[#0A0A0A]/40">—</span>
                          )}
                        </td>
                        <td className="p-2 text-right whitespace-nowrap">
                          <div className="inline-flex items-center gap-1">
                            {ev.has_card ? (
                              <button
                                onClick={() => openCard(ev.card_date || ev.date, ev.card_id)}
                                disabled={busy}
                                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition disabled:opacity-50"
                                data-testid={`open-card-${ev.id}`}
                                title="Apri la scheda nel Mio Giorno"
                              >
                                <ExternalLink size={10} /> Apri
                              </button>
                            ) : (
                              <button
                                onClick={() => createCardFromEvent(ev)}
                                disabled={busy}
                                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider border border-emerald-700 text-emerald-700 hover:bg-emerald-700 hover:text-white transition disabled:opacity-50"
                                data-testid={`create-card-${ev.id}`}
                                title="Crea scheda CRM collegata a questo evento"
                              >
                                {busy ? <Loader2 size={10} className="animate-spin" /> : <Plus size={10} />} Crea scheda
                              </button>
                            )}
                            <button
                              onClick={() => hideEvent(ev)}
                              disabled={busy}
                              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider border border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition disabled:opacity-50"
                              data-testid={`hide-event-${ev.id}`}
                              title="Rimuovi dall'elenco (es. doppione/personale). L'evento resta su Google Calendar."
                            >
                              {busy ? <Loader2 size={10} className="animate-spin" /> : <Trash2 size={10} />} Rimuovi
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

const ExtraStat = ({ label, value }) => (
  <div className="border border-[#0A0A0A]/20 bg-white p-3">
    <div className="text-[9px] uppercase tracking-widest text-[#0A0A0A]/50">{label}</div>
    <div className="font-display text-2xl mt-1 text-[#0A0A0A]">{value}</div>
  </div>
);
